import React, { useState, useEffect } from 'react';
import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    CircularProgress
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'tiff'];

const getExtension = (url) => {
    if (!url) return '';
    try {
        const urlObj = new URL(url, window.location.origin);
        return urlObj.pathname.split('.').pop().toLowerCase();
    } catch (e) {
        const path = url.split('?')[0];
        return path.split('.').pop().toLowerCase();
    }
};

export default function PreviewDialog({ open, setOpen, fileUrl, pdfUrl }) {
    const urlToPreview = fileUrl || pdfUrl;
    const [previewUrl, setPreviewUrl] = useState(null);
    const [fileType, setFileType] = useState(null); // 'pdf' or 'image'
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open || !urlToPreview) {
            setPreviewUrl(null);
            setFileType(null);
            setLoading(false);
            return;
        }

        setLoading(true);
        let active = true;
        let objectUrl = null;

        fetch(urlToPreview)
            .then((res) => {
                if (!res.ok) {
                    throw new Error(`HTTP error! status: ${res.status}`);
                }
                return res.blob();
            })
            .then((blob) => {
                if (!active) return;

                let mimeType = blob.type;
                let detectedType = 'pdf'; // default

                if (mimeType.startsWith('image/')) {
                    detectedType = 'image';
                } else if (mimeType === 'application/pdf') {
                    detectedType = 'pdf';
                } else if (mimeType === 'application/msword' || mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
                    detectedType = 'word';
                } else {
                    const ext = getExtension(urlToPreview);
                    if (ext === 'pdf') {
                        mimeType = 'application/pdf';
                        detectedType = 'pdf';
                    } else if (IMAGE_EXTENSIONS.includes(ext)) {
                        detectedType = 'image';
                    } else if (ext === 'doc' || ext === 'docx') {
                        detectedType = 'word';
                    }
                }

                const finalBlob = (detectedType === 'pdf' && mimeType !== blob.type)
                    ? new Blob([blob], { type: mimeType })
                    : blob;

                objectUrl = URL.createObjectURL(finalBlob);
                setPreviewUrl(objectUrl);
                setFileType(detectedType);
                setLoading(false);
            })
            .catch((err) => {
                console.error('Error fetching preview blob, falling back to direct URL:', err);
                if (!active) return;

                setPreviewUrl(urlToPreview);
                const ext = getExtension(urlToPreview);
                if (IMAGE_EXTENSIONS.includes(ext)) {
                    setFileType('image');
                } else if (ext === 'doc' || ext === 'docx') {
                    setFileType('word');
                } else {
                    setFileType('pdf');
                }
                setLoading(false);
            });

        return () => {
            active = false;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [urlToPreview, open]);

    console.log('urlToPreview', urlToPreview, 'previewUrl', previewUrl, 'fileType', fileType);
    return (
        <>
            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>
                    Preview
                    <IconButton
                        style={{ position: 'absolute', right: 8, top: 8 }}
                        onClick={() => setOpen(false)}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent style={{ height: '80vh', padding: 0 }}>
                    {loading ? (
                        <div style={{ display: 'flex', width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center' }}>
                            <CircularProgress />
                        </div>
                    ) : (
                        previewUrl && (
                            fileType === 'image' ? (
                                <div style={{
                                    display: 'flex',
                                    width: '100%',
                                    height: '100%',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    backgroundColor: '#f5f5f5',
                                    overflow: 'auto',
                                    padding: '16px',
                                    boxSizing: 'border-box'
                                }}>
                                    <img
                                        src={previewUrl}
                                        alt="Preview"
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '100%',
                                            objectFit: 'contain',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                            borderRadius: '4px'
                                        }}
                                    />
                                </div>
                            ) : fileType === 'word' ? (
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    width: '100%',
                                    height: '100%',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    backgroundColor: '#f5f5f5',
                                    padding: '24px',
                                    boxSizing: 'border-box',
                                    textAlign: 'center'
                                }}>
                                    <i className="far fa-file-word" style={{ fontSize: '80px', color: '#2563EB', marginBottom: '20px' }}></i>
                                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1F2937', marginBottom: '10px' }}>
                                        Preview Not Available
                                    </div>
                                    <div style={{ fontSize: '14px', color: '#4B5563', marginBottom: '24px', maxWidth: '400px' }}>
                                        Word documents (.doc, .docx) cannot be previewed directly in the browser. Please download the file to view its contents.
                                    </div>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        href={urlToPreview}
                                        download
                                        style={{
                                            backgroundColor: '#0D9488',
                                            color: '#fff',
                                            textTransform: 'none',
                                            padding: '8px 24px',
                                            borderRadius: '6px',
                                            fontWeight: '600'
                                        }}
                                    >
                                        Download File
                                    </Button>
                                </div>
                            ) : (
                                <iframe
                                    src={previewUrl}
                                    title="PDF Viewer"
                                    width="100%"
                                    height="100%"
                                    style={{ border: 'none' }}
                                />
                            )
                        )
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}